import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import {
  MAX_NOTE_LENGTH,
  VAT_ID_EXCLUSION_REASONS,
  reasonRequiresNote,
} from "../../../../utils/vatIdExclusion";

interface MissingVatIdCustomer {
  id: string;
  company_name?: string;
  account_holder_name?: string;
}

interface RemoveFromListDialogProps {
  customer: MissingVatIdCustomer | null;
  saving: boolean;
  /** Server-side rejection to show inside the dialog, so the form stays open. */
  errorMessage: string | null;
  onConfirm: (reason: string, note: string) => void;
  onClose: () => void;
}

const customerName = (customer: MissingVatIdCustomer) =>
  customer.company_name || customer.account_holder_name || customer.id;

export default function RemoveFromListDialog({
  customer,
  saving,
  errorMessage,
  onConfirm,
  onClose,
}: RemoveFromListDialogProps) {
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState("");

  // Each customer gets a fresh form — a reason left over from the previous row
  // is the kind of thing that gets saved without being read.
  useEffect(() => {
    setReason(null);
    setNote("");
  }, [customer?.id]);

  const noteRequired = reasonRequiresNote(reason);
  const noteMissing = noteRequired && !note.trim();
  const canSave = !!reason && !noteMissing && !saving;

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button label="Cancel" text onClick={onClose} disabled={saving} />
      <Button
        label={saving ? "Removing…" : "Remove from list"}
        icon="pi pi-check"
        severity="danger"
        disabled={!canSave}
        onClick={() => onConfirm(reason as string, note)}
      />
    </div>
  );

  return (
    <Dialog
      header="Remove from the list"
      visible={!!customer}
      onHide={onClose}
      footer={footer}
      style={{ width: "32rem", maxWidth: "95vw" }}
      draggable={false}
    >
      {customer && (
        <div className="flex flex-column gap-3">
          <p className="m-0 text-color-secondary">
            {customerName(customer)} will no longer appear in the list of
            business customers missing a VAT ID. You can put it back later.
          </p>

          <div className="flex flex-column gap-2">
            <label htmlFor="vat-exclusion-reason" className="font-semibold">
              Reason
            </label>
            <Dropdown
              inputId="vat-exclusion-reason"
              value={reason}
              options={[...VAT_ID_EXCLUSION_REASONS]}
              optionLabel="label"
              optionValue="value"
              placeholder="Pick a reason"
              onChange={(e) => setReason(e.value)}
              disabled={saving}
            />
            {reason && (
              <small className="text-color-secondary">
                {VAT_ID_EXCLUSION_REASONS.find((r) => r.value === reason)?.hint}
              </small>
            )}
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="vat-exclusion-note" className="font-semibold">
              Note {noteRequired ? "" : "(optional)"}
            </label>
            <InputTextarea
              id="vat-exclusion-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              autoResize
              maxLength={MAX_NOTE_LENGTH}
              disabled={saving}
              className={noteMissing ? "p-invalid" : ""}
              placeholder={
                noteRequired
                  ? "Explain why this customer will never have a VAT ID"
                  : "Anything worth knowing later"
              }
            />
            <small
              className={
                noteMissing ? "text-red-500" : "text-color-secondary"
              }
            >
              {noteMissing
                ? "A note is required when the reason is Other."
                : `${note.length}/${MAX_NOTE_LENGTH}`}
            </small>
          </div>

          {errorMessage && <Message severity="error" text={errorMessage} />}
        </div>
      )}
    </Dialog>
  );
}
